// Break-even analysis — three independent levers computed at both
// price scenarios (min / max).
//
// Each lever asks: "If I hold the other two revenue streams constant
// at their current values, how much of THIS one would I need to break
// even?" That keeps the answers comparable and intuitive.
//
// Plan A from the design discussion: the % denominator is the sum of
// planned tier quantities the organiser already entered, not a separate
// venue-capacity field. The mental model is "would my current plan, if
// realised, actually cover costs?".

import { formatMoney } from './currency';
import type { EventData } from './types';

export type LeverResult = {
  /** Quantity (or amount, for cost-down) needed to close the gap. */
  needed: number;
  /** Sum of currently planned tier quantities (0 if none). */
  planned: number;
  /** needed / planned. >1 means over-plan. NaN if planned == 0. */
  pctOfPlan: number;
  /** True when the OTHER revenue streams alone already cover costs. */
  cleared: boolean;
  /** False when there's no average price to scale (no qty entered). */
  computable: boolean;
};

export type CostDownResult = {
  needed: number;
  cleared: boolean;
};

export type BreakevenScenario = {
  /** Tickets needed at the weighted-average ticket price for this scenario. */
  tickets: LeverResult;
  /** Sponsors needed at the weighted-average sponsor price for this scenario. */
  sponsors: LeverResult;
  /** Cost reduction needed; 0 when already profitable. */
  costDown: CostDownResult;
  /** revenue >= costs at this price scenario. */
  profitable: boolean;
  /** costs - revenue. Negative when profitable. */
  gap: number;
  /** Total revenue at this price scenario. */
  revenue: number;
};

export type Breakeven = {
  min: BreakevenScenario;
  max: BreakevenScenario;
  costs: number;
};

type Mode = 'min' | 'max';

function ticketRevenue(data: EventData, mode: Mode): number {
  return data.tickets.reduce((acc, t) => {
    const p = mode === 'min' ? t.priceMin : t.priceMax;
    return acc + t.qty * p;
  }, 0);
}

function sponsorRevenue(data: EventData, mode: Mode): number {
  return data.sponsors.reduce((acc, s) => {
    const p = mode === 'min' ? s.priceMin : s.priceMax;
    return acc + s.qty * p;
  }, 0);
}

function addonRevenue(data: EventData): number {
  return data.addons.reduce((acc, a) => {
    if (!a.enabled) return acc;
    const m = a.multiplier && a.multiplier > 0 ? a.multiplier : 1;
    return acc + a.price * m;
  }, 0);
}

function totalCosts(data: EventData): number {
  return Object.values(data.costs).reduce(
    (acc, items) => acc + items.reduce((s, i) => s + i.amount, 0),
    0,
  );
}

function computeScenario(data: EventData, mode: Mode, costs: number): BreakevenScenario {
  const tRev = ticketRevenue(data, mode);
  const sRev = sponsorRevenue(data, mode);
  const aRev = addonRevenue(data);
  const revenue = tRev + sRev + aRev;
  const gap = costs - revenue;
  const profitable = gap <= 0;

  // --- Tickets lever -------------------------------------------------
  const ticketCount = data.tickets.reduce((a, t) => a + t.qty, 0);
  const avgTicketPrice = ticketCount > 0 ? tRev / ticketCount : 0;
  const ticketGap = costs - sRev - aRev; // what tickets must cover
  const ticketsCleared = ticketGap <= 0;
  const ticketsComputable = ticketCount > 0 && avgTicketPrice > 0;
  const ticketsNeeded =
    ticketsCleared || !ticketsComputable
      ? 0
      : Math.ceil(ticketGap / avgTicketPrice);
  const tickets: LeverResult = {
    needed: ticketsNeeded,
    planned: ticketCount,
    pctOfPlan: ticketCount > 0 ? ticketsNeeded / ticketCount : NaN,
    cleared: ticketsCleared,
    computable: ticketsComputable,
  };

  // --- Sponsors lever ------------------------------------------------
  const sponsorCount = data.sponsors.reduce((a, s) => a + s.qty, 0);
  const avgSponsorPrice = sponsorCount > 0 ? sRev / sponsorCount : 0;
  const sponsorGap = costs - tRev - aRev;
  const sponsorsCleared = sponsorGap <= 0;
  const sponsorsComputable = sponsorCount > 0 && avgSponsorPrice > 0;
  const sponsorsNeeded =
    sponsorsCleared || !sponsorsComputable
      ? 0
      : Math.ceil(sponsorGap / avgSponsorPrice);
  const sponsors: LeverResult = {
    needed: sponsorsNeeded,
    planned: sponsorCount,
    pctOfPlan: sponsorCount > 0 ? sponsorsNeeded / sponsorCount : NaN,
    cleared: sponsorsCleared,
    computable: sponsorsComputable,
  };

  // --- Cost-down lever -----------------------------------------------
  const costDown: CostDownResult = {
    needed: profitable ? 0 : gap,
    cleared: profitable,
  };

  return { tickets, sponsors, costDown, profitable, gap, revenue };
}

export function computeBreakeven(data: EventData): Breakeven {
  const costs = totalCosts(data);
  return {
    min: computeScenario(data, 'min', costs),
    max: computeScenario(data, 'max', costs),
    costs,
  };
}

/**
 * One-line summary for the forecast tile. Returns null when the model is
 * empty (no costs and no revenue) so the UI can hide the line.
 */
export type StatusTone = 'good' | 'warn' | 'bad' | 'empty';
export type Status = {
  tone: StatusTone;
  text: string;
};

export function statusLine(b: Breakeven, currency: string): Status | null {
  const empty = b.costs === 0 && b.min.revenue === 0 && b.max.revenue === 0;
  if (empty) return null;

  if (b.min.profitable) {
    return {
      tone: 'good',
      text: `Profitable at min prices · headroom ${formatMoney(-b.min.gap, currency)}`,
    };
  }
  if (b.max.profitable) {
    // Loss at min, profitable at max — describe the closest path to break-even.
    const lever = pickBestLever(b.min);
    return {
      tone: 'warn',
      text: `Profitable only at max prices · ${describeLever(lever, b.min, 'min', currency)}`,
    };
  }
  // Loss at both ends.
  const lever = pickBestLever(b.max);
  return {
    tone: 'bad',
    text: `Below break-even at max prices · ${describeLever(lever, b.max, 'max', currency)}`,
  };
}

type LeverChoice = 'tickets' | 'sponsors' | 'costDown';

function pickBestLever(s: BreakevenScenario): LeverChoice {
  // Prefer whichever lever has a computable, on-plan answer.
  if (s.tickets.computable && s.tickets.pctOfPlan <= 1) return 'tickets';
  if (s.sponsors.computable && s.sponsors.pctOfPlan <= 1) return 'sponsors';
  if (s.tickets.computable) return 'tickets';
  if (s.sponsors.computable) return 'sponsors';
  return 'costDown';
}

function describeLever(
  choice: LeverChoice,
  s: BreakevenScenario,
  mode: 'min' | 'max',
  currency: string,
): string {
  switch (choice) {
    case 'tickets': {
      const pct = Number.isFinite(s.tickets.pctOfPlan)
        ? ` (${Math.round(s.tickets.pctOfPlan * 100)}% of plan)`
        : '';
      return `need ${formatCount(s.tickets.needed)} tickets at ${mode}${pct}`;
    }
    case 'sponsors': {
      const pct = Number.isFinite(s.sponsors.pctOfPlan)
        ? ` (${Math.round(s.sponsors.pctOfPlan * 100)}% of plan)`
        : '';
      return `need ${formatCount(s.sponsors.needed)} sponsors at ${mode}${pct}`;
    }
    case 'costDown':
      return `need ${formatMoney(s.costDown.needed, currency)} less in costs`;
  }
}

function formatCount(n: number): string {
  return n.toLocaleString('en-US').replace(/,/g, ' ');
}
